import { find, flatMap, uniqBy } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTrackEvent } from "../../../../common/api/useTrackEvent";
import { Dropdown, Typography } from "../../../../common/components";
import { Modal } from "../../../../common/components/Modal";
import { ANALYTICS_EVENTS } from "../../../../common/constants/analytics.constants";
import { Item } from "../../../../common/models/item.model";
import { FLOW_TYPES, PREDEFINED_SPACE_TEMPLATES, STEPS } from "../../constants";
import { SpacePurpose, SpaceTemplate } from "../../models";
import { useSetUpSpacesStore } from "../../store";
import { AddNewOptionModal } from "../AddNewOptionModal";
import { DialogStepper } from "../DialogStepper";
import { SpacePurposesTable } from "../SpacePurposesTable";

interface AddSpacePurposesProps {
    spaceTemplates?: SpaceTemplate[];
}

const AddSpacePurposes = (props: AddSpacePurposesProps) => {
    const { spaceTemplates } = props;

    const { t } = useTranslation(["common", "setUpSpaces", "spaces"]);

    const {
        toggleVisibility,
        setStep,
        spaces,
        newSpacePurposeOptions,
        newSubSpacePurposeOptions,
        addNewSpacePurposeOption,
        addNewSubSpacePurposeOption,
        addSpacePurpose,
        addSubSpacePurposeToSpacePurpose,
        spacePurposes,
        flowType,
    } = useSetUpSpacesStore();

    const { trackEvent } = useTrackEvent();

    // Space purpose states
    const [isAddingNewSpacePurpose, setIsAddingNewSpacePurpose] =
        useState(false);

    // SubSpace purpose states
    const [spacePurposeToAddNewSubSpace, setSpacePurposeToAddNewSubSpace] =
        useState<SpacePurpose>();

    // The list of all space purposes containing server data, predefined data and newly added data
    const spacePurposeOptions: Item[] = useMemo(() => {
        // Templates saved in the BE
        const spacePurposeTemplates =
            spaceTemplates?.map(({ spacePurpose }) => ({
                id: spacePurpose.id as string,
                label: spacePurpose.name,
            })) || [];

        // The list of newly added space purposes by the user
        const newSpacePurposesItems = newSpacePurposeOptions.map((item) => ({
            id: item,
            label: item,
        }));

        const predefinedSpacePurposes = Object.keys(PREDEFINED_SPACE_TEMPLATES)
            .map((key) => ({
                id: key,
                label: t(`setUpSpaces:spacePurposes.${key}`),
            }))
            .filter(({ label }) => !find(spacePurposeTemplates, { label }));

        const allOptions = spacePurposeTemplates
            .concat(newSpacePurposesItems)
            .concat(predefinedSpacePurposes);

        return allOptions;
    }, [spaceTemplates, newSpacePurposeOptions, t]);

    const filteredSpacePurposes: Item[] = useMemo(
        () =>
            spacePurposeOptions.filter(
                (option) =>
                    !flatMap(spacePurposes, "name").includes(option.label)
            ),
        [spacePurposeOptions, spacePurposes]
    );

    const subSpacePurposesFromTemplate: Item[] = useMemo(() => {
        return flatMap(spaceTemplates, "subSpacePurposes").map(
            ({ id, name }) => ({ id, label: name })
        );
    }, [spaceTemplates]);

    // The list of all subspace purposes containing server data, predefined data and newly added data
    const subSpacePurposesOptions: Item[] = useMemo(() => {
        // The list of newly added subspace purposes by the user
        const newSpacePurposesItems = newSubSpacePurposeOptions.map((item) => ({
            id: item,
            label: item,
        }));

        const predefinedSubSpacePurposes = flatMap(
            Object.values(PREDEFINED_SPACE_TEMPLATES)
        )
            .map((key) => ({
                id: key,
                label: t(`setUpSpaces:spacePurposes.${key}`),
            }))
            .filter(
                ({ label }) => !find(subSpacePurposesFromTemplate, { label })
            );

        const allOptions = subSpacePurposesFromTemplate
            .concat(newSpacePurposesItems)
            .concat(predefinedSubSpacePurposes);

        return uniqBy(allOptions, "label");
    }, [newSubSpacePurposeOptions, subSpacePurposesFromTemplate, t]);

    const hasSpacePurposes = !!spacePurposes.length;

    const handleClickNext = () => {
        if (!hasSpacePurposes) {
            return;
        }

        setStep(STEPS.ASSIGN_SPACES);
        trackEvent(ANALYTICS_EVENTS.SET_UP_SPACE_PURPOSES, {
            "Count of Rooms": spaces.length,
            Subspaces: flatMap(
                flatMap(spacePurposes, "subSpacePurposes"),
                "name"
            ),
            "Space Purposes": flatMap(spacePurposes, "name"),
        });
    };

    const handleClickBack = () => setStep(STEPS.ADD_SPACES);

    const handleAddNewSpacePurpose = (newSpacePurpose: string) => {
        addNewSpacePurposeOption(newSpacePurpose);
        addSpacePurpose({
            name: newSpacePurpose,
            subSpacePurposes: [],
            spaces: [],
        });
    };

    const handleAddNewSubSpacePurpose = (newSubSpacePurpose: string) => {
        addNewSubSpacePurposeOption(newSubSpacePurpose);
        if (!spacePurposeToAddNewSubSpace) {
            return;
        }
        addSubSpacePurposeToSpacePurpose(spacePurposeToAddNewSubSpace, {
            name: newSubSpacePurpose,
        });
    };

    const handleAddSpacePurpose = (selectedSpacePurpose?: Item) => {
        if (!selectedSpacePurpose) {
            return;
        }

        const spaceTemplate = find(spaceTemplates, {
            spacePurpose: { id: selectedSpacePurpose.id },
        });

        const predefinedSubSpacePurposes =
            //@ts-ignore
            PREDEFINED_SPACE_TEMPLATES[selectedSpacePurpose.id]?.map(
                (key: string) => ({
                    name: t(`setUpSpaces:spacePurposes.${key}`),
                })
            ) || [];

        addSpacePurpose({
            spaceTemplateId: spaceTemplate?.id,
            id: spaceTemplate?.spacePurpose.id,
            name: selectedSpacePurpose.label,
            subSpacePurposes: spaceTemplate
                ? spaceTemplate.subSpacePurposes
                : predefinedSubSpacePurposes,
            spaces: [],
        });
    };

    const getTitle = () => {
        if (flowType === FLOW_TYPES.ADD_SPACES) {
            return t("spaces:addNewSpaces");
        }
        return t("setUpSpaces:spacePurposes.title");
    };

    return (
        <>
            <Modal
                isOpen
                title={getTitle()}
                hasCancelButton
                hasOkButton
                cancelText={t("common:back") || ""}
                okText={t("common:next") || ""}
                className="h-fit max-w-full"
                onClickOk={handleClickNext}
                onClickCancel={handleClickBack}
                onClickClose={toggleVisibility}
                isOkButtonDisabled={!hasSpacePurposes}
            >
                <div className="flex flex-col gap-4 [&_.p-datatable-wrapper]:overflow-y-hidden">
                    <DialogStepper activeStep={STEPS.ADD_SPACE_PURPOSES} />
                    <Typography>
                        {t("setUpSpaces:spacePurposes.intro")}
                    </Typography>
                    <h3 className="text-lg">
                        {t("setUpSpaces:spacePurposes.subtitle")}
                    </h3>
                    <Typography className="font-light">
                        {t("setUpSpaces:spacePurposes.description")}
                    </Typography>

                    <SpacePurposesTable
                        openAddNewSubSpace={setSpacePurposeToAddNewSubSpace}
                        subSpacePurposesOptions={subSpacePurposesOptions}
                    />

                    <div className="flex flex-row gap-5 items-end">
                        <Dropdown
                            onChange={(e) => {
                                handleAddSpacePurpose(
                                    find(filteredSpacePurposes, ["id", e.value])
                                );
                            }}
                            options={filteredSpacePurposes}
                            optionValue="id"
                            optionLabel="label"
                            placeholder={
                                t(
                                    "setUpSpaces:spacePurposes.selectSpacePurpose"
                                ) || ""
                            }
                            emptyMessage={t("common:noResultsFound")}
                            filter
                            panelFooterTemplate={
                                <div
                                    className="flex flex-row items-center px-5 py-3 gap-2 cursor-pointer hover:bg-surface-5 transition-colors duration-200 overflow-hidden border-t"
                                    onClick={() =>
                                        setIsAddingNewSpacePurpose(true)
                                    }
                                >
                                    <i className="pi pi-plus" />
                                    {t(
                                        "setUpSpaces:spacePurposes.addSpacePurpose"
                                    )}
                                </div>
                            }
                            pt={{
                                panel: {
                                    className: "overflow-hidden",
                                },
                            }}
                            resetFilterOnHide
                        />
                    </div>
                </div>
            </Modal>
            <AddNewOptionModal
                isOpen={isAddingNewSpacePurpose}
                options={spacePurposeOptions}
                onAddNewOption={handleAddNewSpacePurpose}
                onCancel={() => setIsAddingNewSpacePurpose(false)}
                labels={{
                    title: t("setUpSpaces:spacePurposes.addSpacePurpose") || "",
                    description:
                        t("setUpSpaces:spacePurposes.createSpacePurpose") || "",
                    label:
                        t("setUpSpaces:spacePurposes.spacePurposeName") || "",
                    errorMessage:
                        t("setUpSpaces:spacePurposes.spacePurposeError") || "",
                }}
            />
            <AddNewOptionModal
                isOpen={!!spacePurposeToAddNewSubSpace}
                options={subSpacePurposesOptions}
                onAddNewOption={handleAddNewSubSpacePurpose}
                onCancel={() => setSpacePurposeToAddNewSubSpace(undefined)}
                labels={{
                    title: t("setUpSpaces:spacePurposes.addNewSubSpace") || "",
                    description:
                        t("setUpSpaces:spacePurposes.createSubSpace") || "",
                    label: t("setUpSpaces:spacePurposes.subSpaceName") || "",
                    errorMessage:
                        t("setUpSpaces:spacePurposes.subSpaceError") || "",
                }}
            />
        </>
    );
};

export default AddSpacePurposes;
